/*
  Horizontal scrollers for the search modal.

  Markup contract:
    <div data-scroller>            wrapper holding the buttons and the list
      <button data-scroll-prev>
      <button data-scroll-next>
      <ul data-scroll-list>        the element that actually scrolls

  data-scroll-list is what makes this work in every grid rather than one:
  looking for a `ul` misses the recently viewed track, which is a <div>, and
  can land on a list inside a product card.

  The list only moves if CSS gives it `overflow-x: auto` and stops it wrapping.
*/
(function () {
  'use strict';

  var SCROLLER = '[data-scroller]';
  var ARROWS = '[data-scroll-prev], [data-scroll-next]';

  /* scrollLeft lands a fraction of a pixel off its target often enough that an
     exact comparison leaves the end arrow permanently enabled. */
  var EDGE_TOLERANCE = 2;

  function isScrollable(element) {
    var overflowX = window.getComputedStyle(element).overflowX;
    if (overflowX !== 'auto' && overflowX !== 'scroll') return false;
    return element.scrollWidth - element.clientWidth > EDGE_TOLERANCE;
  }

  function listFor(scroller) {
    var marked = scroller.querySelector('[data-scroll-list]');
    if (marked) return marked;

    var candidates = scroller.querySelectorAll('ul, ol, div');
    for (var i = 0; i < candidates.length; i++) {
      if (isScrollable(candidates[i])) return candidates[i];
    }
    return null;
  }

  function refresh(scroller) {
    var list = listFor(scroller);
    if (!list) return;

    /* A section still carrying `hidden` measures zero for everything, which
       would disable both arrows for good once search-gender.js reveals it. */
    if (list.clientWidth === 0) return;

    var furthest = list.scrollWidth - list.clientWidth;
    var overflows = furthest > EDGE_TOLERANCE;

    var prev = scroller.querySelector('[data-scroll-prev]');
    var next = scroller.querySelector('[data-scroll-next]');

    if (prev) prev.disabled = !overflows || list.scrollLeft <= EDGE_TOLERANCE;
    if (next) next.disabled = !overflows || list.scrollLeft >= furthest - EDGE_TOLERANCE;

    scroller.toggleAttribute('data-scroller-overflows', overflows);
  }

  function setup(scroller) {
    if (scroller.dataset.scrollerReady === 'true') return;
    scroller.dataset.scrollerReady = 'true';

    var list = listFor(scroller);
    if (list) {
      list.addEventListener(
        'scroll',
        function () {
          refresh(scroller);
        },
        { passive: true }
      );

      if (window.ResizeObserver) {
        new window.ResizeObserver(function () {
          refresh(scroller);
        }).observe(list);
      }

      /* Recently viewed fills its track from a fetch, long after this runs. */
      new MutationObserver(function () {
        refresh(scroller);
      }).observe(list, { childList: true });
    }

    refresh(scroller);
  }

  function refreshAll() {
    var scrollers = document.querySelectorAll(SCROLLER);
    for (var i = 0; i < scrollers.length; i++) {
      setup(scrollers[i]);
      refresh(scrollers[i]);
    }
  }

  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    var raf = window.requestAnimationFrame || function (fn) { setTimeout(fn, 16); };
    raf(function () {
      scheduled = false;
      refreshAll();
    });
  }

  function onClick(event) {
    var button = event.target.closest ? event.target.closest(ARROWS) : null;
    if (!button) return;

    event.preventDefault();

    var scroller = button.closest(SCROLLER);
    var list = scroller && listFor(scroller);
    if (!list) return;

    var direction = button.hasAttribute('data-scroll-prev') ? -1 : 1;
    list.scrollBy({ left: list.clientWidth * direction, behavior: 'smooth' });
  }

  /*
    The modal focuses the search input as it opens, so the first arrow click
    hands focus to a button the browser then scrolls into view — which reads as
    the page jumping to the top, exactly once per open. Declining the default
    mousedown leaves focus in the input. Tabbing to the arrows still works.
  */
  function onMousedown(event) {
    var button = event.target.closest ? event.target.closest(ARROWS) : null;
    if (button) event.preventDefault();
  }

  function init() {
    refreshAll();

    document.addEventListener('click', onClick);
    document.addEventListener('mousedown', onMousedown);
    window.addEventListener('resize', schedule);

    /* Catches the gender sections losing `hidden`, predictions re-rendering,
       and any scroller added to the page after load. */
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributeFilter: ['hidden']
    });
  }

  var api = { listFor: listFor, refresh: refresh, setup: setup, EDGE_TOLERANCE: EDGE_TOLERANCE };

  if (typeof window !== 'undefined') window.SearchScroller = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  if (typeof document === 'undefined') return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();